import React, { useState, useEffect } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import { SaveIcon } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import NewsInputField from '../components/NewsInputField';

const InputField: React.FC<{ label: string; value: string; onChange: (val: string) => void; disabled?: boolean, type?: string }> = ({ label, value, onChange, disabled, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-slate-400 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full disabled:bg-slate-800 disabled:text-slate-400 disabled:cursor-not-allowed"
    />
  </div>
);

const CounterDistributionScreen: React.FC = () => {
  const { selectedFlightData: flightData, selectedFlightSlotId, updateCounterDistributionField, saveCurrentFlight, showNotification } = useFlight();
  const { user } = useAuth();
  const [flightNumbers, setFlightNumbers] = useState<string[]>([]);
  
  const booking = flightData?.counterDistribution.booking || { c: '', m: '', inf: '', totalPassengers: '0' };
  const finalFigures = flightData?.counterDistribution.finalFigures || { c: '', m: '', inf: '', totalPassengers: '0', 'Total Bags': '', 'Crew Bags': '', 'Waved Bags': '', 'Wheel Chairs': '', 'Buggy': '', 'Final Fuel': '' };

  useEffect(() => {
    const flightNumbersRef = ref(database, 'config/flightNumbers');
    const unsubscribe = onValue(flightNumbersRef, (snapshot) => {
        setFlightNumbers(snapshot.val() || []);
    });
    return () => unsubscribe();
  }, []);
  
  // Auto-calculate Booking Total Passengers
  useEffect(() => {
    const c = parseInt(booking.c, 10) || 0;
    const m = parseInt(booking.m, 10) || 0;
    const inf = parseInt(booking.inf, 10) || 0;
    const total = c + m + inf;
    if (flightData && String(total) !== booking.totalPassengers) {
      updateCounterDistributionField('booking', 'totalPassengers', String(total));
    }
  }, [booking.c, booking.m, booking.inf, booking.totalPassengers, updateCounterDistributionField, flightData]);

  // Auto-calculate Final Figures Total Passengers
  useEffect(() => {
    const c = parseInt(finalFigures.c, 10) || 0;
    const m = parseInt(finalFigures.m, 10) || 0;
    const inf = parseInt(finalFigures.inf, 10) || 0;
    const total = c + m + inf;
    if (flightData && String(total) !== finalFigures.totalPassengers) {
      updateCounterDistributionField('finalFigures', 'totalPassengers', String(total));
    }
  }, [finalFigures.c, finalFigures.m, finalFigures.inf, finalFigures.totalPassengers, updateCounterDistributionField, flightData]);
  
  if (!flightData) return null;

  const { counterDistribution } = flightData;
  const etd = counterDistribution.etd || '';
  const etdDate = etd.split('T')[0] || '';
  const etdTime = etd.split('T')[1] || '';

  const handleDateChange = (date: string) => {
    const currentTime = etd.split('T')[1] || '00:00';
    if (date) {
      updateCounterDistributionField('root', 'etd', `${date}T${currentTime}`);
    } else {
      updateCounterDistributionField('root', 'etd', '');
    }
  };

  const handleTimeChange = (time: string) => {
    const currentDate = etd.split('T')[0];
    if (currentDate && time) {
      updateCounterDistributionField('root', 'etd', `${currentDate}T${time}`);
    }
  };
  
  const handleSaveOnly = async () => {
    if (!selectedFlightSlotId) {
        showNotification('Cannot save report: No flight is selected.', 'error');
        return;
    }
    const result = await saveCurrentFlight(selectedFlightSlotId);
    showNotification(result.message, result.success ? 'success' : 'error');
  };

  const formattedDate = etdDate ? new Date(etdDate).toLocaleDateString('en-GB', {timeZone: 'UTC'}) : null;
  
  return (
    <div className="space-y-6">
       <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
         <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-grow">
          <div>
            <label htmlFor="flightNo" className="block text-sm font-medium text-slate-400 mb-1">Flight No</label>
            <select
              id="flightNo"
              value={counterDistribution.flightNo}
              onChange={(e) => updateCounterDistributionField('root', 'flightNo', e.target.value)}
              className="w-full"
            >
              <option value="" disabled>Select a flight</option>
              {flightNumbers.map(fn => <option key={fn} value={fn}>{fn}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="etdDate" className="block text-sm font-medium text-slate-400 mb-1">ETD Date</label>
            <input
              type="date"
              id="etdDate"
              value={etdDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full"
            />
            {formattedDate && <p className="text-xs text-slate-500 mt-1">Selected: {formattedDate}</p>}
          </div>
          <div>
            <label htmlFor="etdTime" className="block text-sm font-medium text-slate-400 mb-1">ETD Time</label>
            <input
              type="time"
              id="etdTime"
              value={etdTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="w-full"
            />
          </div>
         </div>
         <p className="text-slate-400 text-sm mt-4 text-center">Manage flight distribution details. All fields are always editable.</p>
       </div>

      {user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && (
        <NewsInputField />
      )}

      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h3 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Booking Figures</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <InputField label="C" type="number" value={booking.c} onChange={(val) => updateCounterDistributionField('booking', 'c', val)} />
              <InputField label="M" type="number" value={booking.m} onChange={(val) => updateCounterDistributionField('booking', 'm', val)} />
              <InputField label="inf" type="number" value={booking.inf} onChange={(val) => updateCounterDistributionField('booking', 'inf', val)} />
              <InputField label="Total Passengers" value={booking.totalPassengers} onChange={() => {}} disabled />
          </div>
      </div>

      <div data-tour-id="counter-distribution-inputs" className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h3 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Counter Distribution</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.keys(counterDistribution.counters).map(key => (
            <InputField key={key} label={key} value={counterDistribution.counters[key]} onChange={(val) => updateCounterDistributionField('counters', key, val)} />
          ))}
        </div>
      </div>
      
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h3 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Gate Assignments</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.keys(counterDistribution.gates).map(key => (
            <InputField key={key} label={key} value={counterDistribution.gates[key]} onChange={(val) => updateCounterDistributionField('gates', key, val)} />
          ))}
        </div>
      </div>

      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h3 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Final Figures</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <InputField label="C" type="number" value={finalFigures.c} onChange={(val) => updateCounterDistributionField('finalFigures', 'c', val)} />
              <InputField label="M" type="number" value={finalFigures.m} onChange={(val) => updateCounterDistributionField('finalFigures', 'm', val)} />
              <InputField label="inf" type="number" value={finalFigures.inf} onChange={(val) => updateCounterDistributionField('finalFigures', 'inf', val)} />
              <InputField label="Total Passengers" value={finalFigures.totalPassengers} onChange={() => {}} disabled />
              <InputField label="Total Bags" type="number" value={finalFigures['Total Bags']} onChange={(val) => updateCounterDistributionField('finalFigures', 'Total Bags', val)} />
              <InputField label="Crew Bags" type="number" value={finalFigures['Crew Bags']} onChange={(val) => updateCounterDistributionField('finalFigures', 'Crew Bags', val)} />
              <InputField label="Waved Bags" type="number" value={finalFigures['Waved Bags']} onChange={(val) => updateCounterDistributionField('finalFigures', 'Waved Bags', val)} />
              <InputField label="Wheel Chairs" type="number" value={finalFigures['Wheel Chairs']} onChange={(val) => updateCounterDistributionField('finalFigures', 'Wheel Chairs', val)} />
              <InputField label="Buggy" type="number" value={finalFigures['Buggy']} onChange={(val) => updateCounterDistributionField('finalFigures', 'Buggy', val)} />
          </div>
      </div>

      {user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <div className="flex justify-end gap-4">
            <button 
              onClick={handleSaveOnly} 
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center text-sm"
            >
              <SaveIcon className="w-5 h-5 mr-2" />
              Save Report Only
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-right">
            This will save the current flight data as a report without starting a new session.
          </p>
        </div>
      )}
    </div>
  );
};

export default CounterDistributionScreen;
